import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaSearch, FaUsers, FaComment, FaHotjar, FaRegNewspaper, FaQuestion, FaExternalLinkAlt } from 'react-icons/fa';

const Community = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  
  // Mock forum data
  const forumTopics = [
    {
      id: 1,
      title: "Welcome to StreamerWarZ Community",
      author: "StreamerWarZ Team",
      category: "announcements",
      replies: 24,
      views: 2405,
      lastActive: "2 hours ago",
      isPinned: true,
      isHot: true
    },
    {
      id: 2,
      title: "Streaming Setup Help & Recommendations",
      author: "StreamMaster42",
      category: "help",
      replies: 134,
      views: 8732,
      lastActive: "20 minutes ago",
      isPinned: false,
      isHot: true
    },
    {
      id: 3,
      title: "April Tournament Feedback Thread",
      author: "GamersUnite",
      category: "tournaments",
      replies: 87,
      views: 3201,
      lastActive: "1 day ago",
      isPinned: false,
      isHot: false
    },
    {
      id: 4,
      title: "Looking for Team Members - Valorant",
      author: "ShotCallerPro",
      category: "teams",
      replies: 42,
      views: 1205,
      lastActive: "3 hours ago",
      isPinned: false,
      isHot: false
    },
    {
      id: 5,
      title: "Stream Overlay Templates - Free Resource",
      author: "DesignWizard",
      category: "resources",
      replies: 56,
      views: 4102,
      lastActive: "5 hours ago",
      isPinned: false,
      isHot: true
    },
    {
      id: 6,
      title: "How to Grow Your Audience - Tips & Tricks",
      author: "StreamCoach",
      category: "guides",
      replies: 112,
      views: 7893,
      lastActive: "8 hours ago",
      isPinned: false,
      isHot: true
    },
    {
      id: 7,
      title: "Weekly Game Night Announcement",
      author: "CommunityManager",
      category: "events",
      replies: 31,
      views: 1487,
      lastActive: "10 hours ago",
      isPinned: true,
      isHot: false
    },
    {
      id: 8,
      title: "Streaming Tech Troubleshooting",
      author: "TechSupport",
      category: "help",
      replies: 95,
      views: 3562,
      lastActive: "1 hour ago",
      isPinned: false,
      isHot: false
    }
  ];
  
  // Filter forum topics based on search query and filter selection
  const filteredTopics = forumTopics.filter(topic => {
    // Filter by search query
    const matchesSearch = 
      topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topic.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by category
    const matchesFilter = 
      filter === 'all' || 
      (filter === 'hot' && topic.isHot) ||
      (filter === 'announcements' && topic.category === 'announcements') ||
      (filter === 'help' && topic.category === 'help');
    
    return matchesSearch && matchesFilter;
  });
  
  // Get category badge color
  const getCategoryBadge = (category) => {
    switch(category) {
      case 'announcements':
        return 'bg-red-600/30 text-red-300';
      case 'help':
        return 'bg-blue-600/30 text-blue-300';
      case 'tournaments':
        return 'bg-purple-600/30 text-purple-300';
      case 'teams':
        return 'bg-green-600/30 text-green-300';
      case 'resources':
        return 'bg-teal-600/30 text-teal-300';
      case 'guides':
        return 'bg-yellow-600/30 text-yellow-300';
      case 'events':
        return 'bg-orange-600/30 text-orange-300';
      default:
        return 'bg-gray-600/30 text-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-harmony-blue">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.h1 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-3xl font-bold text-harmony-white mb-6"
        >
          Community
        </motion.h1>
        
        {/* Search and filter section */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-harmony-white/50" />
            </div>
            <input
              type="text"
              placeholder="Search topics, authors, or categories..."
              className="w-full bg-harmony-blue-dark border border-harmony-white/10 text-harmony-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-harmony-orange"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button 
              onClick={() => setFilter('all')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                filter === 'all' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              } transition-colors`}
            >
              <FaUsers className="mr-2" /> All Topics
            </button>
            <button 
              onClick={() => setFilter('hot')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                filter === 'hot' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              } transition-colors`}
            >
              <FaHotjar className="mr-2" /> Hot Topics
            </button>
            <button 
              onClick={() => setFilter('announcements')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                filter === 'announcements' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              } transition-colors`}
            >
              <FaRegNewspaper className="mr-2" /> Announcements
            </button>
            <button 
              onClick={() => setFilter('help')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                filter === 'help' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              } transition-colors`}
            >
              <FaQuestion className="mr-2" /> Help & Support
            </button>
          </div>
        </div>
        
        {/* Forum topics */}
        {filteredTopics.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {filteredTopics.map((topic, index) => (
              <motion.div
                key={topic.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05 * (index % 8) }}
                className="bg-harmony-blue-dark rounded-lg overflow-hidden border border-harmony-white/10 shadow-lg hover:shadow-2xl transition-all hover:border-harmony-orange/30"
              >
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                    <div className="flex items-start">
                      {topic.isPinned && (
                        <div className="bg-harmony-orange/20 text-harmony-orange text-xs px-2 py-1 rounded mr-2 uppercase font-bold">
                          Pinned
                        </div>
                      )}
                      {topic.isHot && (
                        <div className="bg-red-600/20 text-red-400 text-xs px-2 py-1 rounded mr-2 uppercase font-bold flex items-center">
                          <FaHotjar className="mr-1" /> Hot
                        </div>
                      )}
                      <div className={`text-xs px-2 py-1 rounded mr-2 ${getCategoryBadge(topic.category)}`}>
                        {topic.category}
                      </div>
                    </div>
                    <div className="text-harmony-white/50 text-sm mt-2 md:mt-0">
                      Last active: {topic.lastActive}
                    </div>
                  </div>
                  
                  <Link to={`/community/topic/${topic.id}`} className="block">
                    <h3 className="text-xl font-semibold text-harmony-white mb-2 hover:text-harmony-orange transition-colors">
                      {topic.title}
                    </h3>
                  </Link>
                  
                  <div className="text-harmony-white/70 mb-4">
                    Posted by <span className="text-harmony-orange">{topic.author}</span>
                  </div>
                  
                  <div className="flex items-center mt-4 pt-4 border-t border-harmony-white/10">
                    <div className="flex items-center text-harmony-white/60 mr-6">
                      <FaComment className="mr-2" /> {topic.replies} replies
                    </div>
                    <div className="flex items-center text-harmony-white/60">
                      <FaUsers className="mr-2" /> {topic.views} views
                    </div>
                    <div className="ml-auto">
                      <Link 
                        to={`/community/topic/${topic.id}`}
                        className="px-4 py-2 bg-harmony-blue/30 text-harmony-white rounded-lg hover:bg-harmony-blue/40 transition-colors flex items-center"
                      >
                        <FaExternalLinkAlt className="mr-2" /> View Topic
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-harmony-white/50 text-5xl mb-4">
              <FaComment />
            </div>
            <h3 className="text-harmony-white text-xl font-medium mb-2">No topics found</h3>
            <p className="text-harmony-white/70">Try adjusting your search or filter settings.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Community; 